package com.eladfinish.bible.contest.screens.search.ui

import androidx.compose.material3.AlertDialog
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import com.eladfinish.bible.contest.SharedRes
import com.eladfinish.bible.contest.interesting_facts
import com.eladfinish.bible.contest.screens.mikud.ui.dialogs.SimpleMessageDialog
import com.eladfinish.bible.contest.screens.names.ui.ChapterDialog
import com.eladfinish.bible.contest.screens.search.mvi.SearchAction
import com.eladfinish.bible.contest.screens.search.mvi.SearchAction.ExampleClicked
import com.eladfinish.bible.contest.screens.search.mvi.SearchAction.ToggleHelp
import com.eladfinish.bible.contest.screens.search.mvi.SearchState
import com.eladfinish.bible.contest.screens.search.ui.dialogs.AiSearchInputDialog
import com.eladfinish.bible.contest.screens.search.ui.dialogs.CmpProgressDialog
import com.eladfinish.bible.contest.screens.search.ui.dialogs.CopyOptionsDialogComponent
import com.eladfinish.bible.contest.screens.search.ui.dialogs.LoadingMessageDialog
import com.eladfinish.bible.contest.screens.search.ui.dialogs.PopularSearchesDialog
import com.eladfinish.bible.contest.screens.search.ui.dialogs.ResultsByBookSheet
import com.eladfinish.bible.contest.screens.search.ui.dialogs.SearchHelpDialog
import com.eladfinish.bible.contest.screens.search.ui.dialogs.SearchHistoryDialog
import com.eladfinish.bible.contest.screens.search.usecases.specialSearch.SearchFact.Companion.plusExtraData
import com.eladfinish.bible.contest.ui.components.dialogs.MenuData
import com.eladfinish.bible.contest.ui.components.dialogs.MenuDialog
import dev.icerock.moko.resources.compose.localized
import dev.icerock.moko.resources.desc.Resource
import dev.icerock.moko.resources.desc.StringDesc

@Composable
fun SearchDialogs(
    dialogState: SearchState.DialogState,
    onAction: (SearchAction) -> Unit,
) {
    val onDismiss = {
        onAction(SearchAction.HideDialog)
    }

    when (dialogState) {
        SearchState.DialogState.None -> Unit

        is SearchState.DialogState.RegexInfo -> {
            AlertDialog(
                onDismissRequest = onDismiss,
                confirmButton = {
                    TextButton(onClick = onDismiss) {
                        Text("סגור")
                    }
                },
                title = {
                    Text("מידע על החיפוש")
                },
                text = {
                    Text(
                        text = """
                            Regex:
                            ${dialogState.regex}

                            מספר תוצאות:
                            ${dialogState.resultsCount}
                        """.trimIndent(),
                    )
                },
            )
        }

        is SearchState.DialogState.ResultsByBook -> {
            ResultsByBookSheet(
                items = dialogState.items,
                onBookClick = { bookIndex ->
                    onAction(SearchAction.ResultsByBookItemClicked(bookIndex))
                },
                onDismiss = onDismiss,
            )
        }

        is SearchState.DialogState.General -> {
            SimpleMessageDialog(
                onDismiss = {
                    onAction(SearchAction.HideDialog)
                },
                title = dialogState.title.localized(),
                content = dialogState.message.localized(),
            )
        }

        is SearchState.DialogState.Help -> {
            SearchHelpDialog(
                helpDialogClickableTextData = dialogState.helpDialogClickableTextData,
                isDontShowAgainChecked = dialogState.isDontShowAgainChecked,
                onExit = {
                    onDismiss()
                },
                onApproveDialog = {
                    onAction(ToggleHelp(shouldShowNextTime = it))
                },
                onDismiss = onDismiss,
                onItemClick = {
                    onAction(ExampleClicked(it))
                },
            )
        }

        is SearchState.DialogState.AiSearch -> {
            AiSearchInputDialog(
                onDismiss = {
                    onAction(
                        SearchAction.HideDialog,
                    )
                },
                onConfirm = {
                    onAction(
                        SearchAction.AiPrompt.Submit(text = it),
                    )
                },
                initialValue = dialogState.lastPrompt,
                isLoading = dialogState.isLoading,
            )
        }

        is SearchState.DialogState.CopyOptionsDialog -> {
            CopyOptionsDialogComponent(
                dialogState = dialogState,
                onAction = onAction,
            )
        }

        is SearchState.DialogState.ExecutingLocalSearch -> {
            CmpProgressDialog(
                title = dialogState.title.localized() + dialogState.titleExtraData?.let { data -> " ${data.localized()}" }.orEmpty(),
                message = dialogState.message.localized(),
                progress = dialogState.progress,
                maxValue = dialogState.totalChapters,
                showNativeAd = dialogState.showNativeAd,
                onCancel = {
                    onAction(SearchAction.StopSearching)
                    onAction(SearchAction.HideDialog)
                },
            )
        }

        is SearchState.DialogState.ExecutingRemoteSearch -> {
            LoadingMessageDialog(
                sentence = dialogState.sentence,
                onDismiss = {
                    onAction(SearchAction.HideDialog)
                },
            )
        }

        is SearchState.DialogState.FullChapter -> {
            ChapterDialog(
                chapterData = dialogState.chapterData,
                withArrows = true,
                onPreviousClick = {
                    onAction(SearchAction.ShowPreviousChapter)
                },
                onNextClick = {
                    onAction(SearchAction.ShowNextChapter)
                },
                onDismiss = {
                    onAction(SearchAction.HideDialog)
                },
            )
        }

        is SearchState.DialogState.PopularSearches -> {
            PopularSearchesDialog(
                filter = dialogState.filter,
                entries = dialogState.entries,
                recentDays = dialogState.recentDays,
                onAction = onAction,
                onDismiss = onDismiss,
            )
        }

        is SearchState.DialogState.History -> {
            SearchHistoryDialog(
                entries = dialogState.entries,
                sort = dialogState.sort,
                isConfirmingClear = dialogState.isConfirmingClear,
                onAction = onAction,
                onDismiss = onDismiss,
            )
        }

        is SearchState.DialogState.InterestingFactsMenu -> {
            MenuDialog(
                menuData = MenuData(
                    title = StringDesc.Resource(SharedRes.strings.interesting_facts),
                    items = dialogState.items,
                ),
                itemAsString = {
                    it.stringDesc.localized().plusExtraData(it)
                },
                onItemClick = { _, factItemMenu ->
                    onAction(SearchAction.InterestingFactClicked(searchFact = factItemMenu))
                    onAction(SearchAction.HideDialog)
                },
                onDismiss = {
                    onAction(SearchAction.HideDialog)
                },
            )
        }
    }
}
